import React, { useCallback, useContext, useMemo } from 'react';

import Dropdown from 'components/Inputs/Dropdown';
import Row from 'components/Common/Row';
import { WeeklyBonusOverrideContext } from 'components/DriverAX/providers/WeeklyBonusOverrideProvider';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';

import WeeklyClearOverrideControl from './WeeklyClearOverrideControl';

/**
 * A single week's eligibility (qualifier) override control inside the sub-period
 * fact table — the `qualifiesForBonus` column in override mode. Mirrors the
 * single-record SetBonusEligibilityOverride (Qualified / Disqualified, plus a
 * Baseline option to revert a persisted override), but stages a per-week
 * qualifier override via WeeklyBonusOverrideContext; confirm fans out one PUT per
 * changed week. Unlike the reward cell there is no category/qualified gate — a
 * disqualified week is exactly the one you'd override to qualified.
 */
const WeeklyEligibilityOverrideCell = ({
  bonusDatasetRecordId,
}: {
  bonusDatasetRecordId: string;
}) => {
  const { byRecordId, stageWeekQualifier } = useContext(
    WeeklyBonusOverrideContext,
  );
  const week = byRecordId.get(bonusDatasetRecordId);

  const stagedQualifier = week?.stagedQualifier;
  const persistedQualifier = week?.persistedQualifier;

  // Effective eligibility after staged ⊕ persisted overrides (from the provider).
  const effectiveQualified = !!week?.isEffectivelyQualified;

  const stage = useCallback(
    (qualifier: BonusTypes.Overrides.UserInputQualifier | undefined) => {
      stageWeekQualifier({ bonusDatasetRecordId, qualifier });
    },
    [stageWeekQualifier, bonusDatasetRecordId],
  );

  const renderLabel = useCallback(
    (qualified: boolean) => (
      <Row centerAlign style={{ gap: 8 }}>
        <Icon
          icon={qualified ? 'circleCheckmark' : 'warning'}
          color={
            qualified
              ? 'var(--color-status-success)'
              : 'var(--color-status-danger)'
          }
        />
        <Typography.Body type={'Button Text'}>
          {qualified ? 'Qualified' : 'Disqualified'}
        </Typography.Body>
      </Row>
    ),
    [],
  );

  const options = useMemo((): DropdownOption[] => {
    const base: DropdownOption[] = [
      {
        key: 'qualified',
        label: 'Qualified',
        renderLabel: () => renderLabel(true),
        isSelected: effectiveQualified && stagedQualifier?.action !== 'Clear',
        onSelected: () => stage({ action: 'Set', value: true, note: '' }),
      },
      {
        key: 'disqualified',
        label: 'Disqualified',
        renderLabel: () => renderLabel(false),
        isSelected: !effectiveQualified && stagedQualifier?.action !== 'Clear',
        onSelected: () => stage({ action: 'Set', value: false, note: '' }),
      },
    ];
    // Revert a persisted override back to the computed eligibility.
    if (persistedQualifier) {
      base.push({
        key: 'baseline',
        label: 'Baseline',
        isSelected: stagedQualifier?.action === 'Clear',
        onSelected: () => stage({ action: 'Clear' }),
      });
    }
    return base;
  }, [
    effectiveQualified,
    stagedQualifier,
    persistedQualifier,
    renderLabel,
    stage,
  ]);

  const renderSelectedLabel = useCallback(
    () => renderLabel(effectiveQualified),
    [renderLabel, effectiveQualified],
  );

  if (!week) {
    return null;
  }

  return (
    <Row centerAlign style={{ gap: 4, justifyContent: 'flex-end' }}>
      <Dropdown
        options={options}
        renderSelectedLabel={renderSelectedLabel}
        isDisabled={week.isPreviewLoading}
        unsetWidth
        size={'Small'}
        testId={`weekly-eligibility-override-${bonusDatasetRecordId}-dropdown`}
      />
      {!!persistedQualifier && (
        <WeeklyClearOverrideControl
          isCleared={stagedQualifier?.action === 'Clear'}
          onClear={() => stage({ action: 'Clear' })}
          onUndo={() => stage(undefined)}
          testId={`weekly-eligibility-clear-${bonusDatasetRecordId}`}
        />
      )}
    </Row>
  );
};

export default WeeklyEligibilityOverrideCell;
